import { ArrowRightIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Navbar } from "../../components/common/Navbar";
import { ChallengeDialog } from "../../components/challenges";
import { useAuth } from "../../context/AuthContext";

export const Home = (): JSX.Element => {
  const navigate = useNavigate();
  const { justRegistered, setJustRegistered } = useAuth();
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);

  // Feature card data with illustration images
  const featureCards = [
    {
      title: "Lesson Plan Builder",
      description: "Buat rencana pembelajaran yang terstruktur dan adaptif",
      linkText: "Mulai Buat",
      linkColor: "text-[#951be9]",
      bgColor: "bg-[#f3e4ff]",
      illustrationImage: "/lesson-plan-illustration.png",
      route: "/lesson-plan-builder",
    },
    {
      title: "Assessment Builder",
      description: "Rancang soal yang komprehensif dan objektif",
      linkText: "Mulai Buat",
      linkColor: "text-[#0217ff]",
      bgColor: "bg-[#e4e6ff]",
      illustrationImage: "/assessment-builder-illustration.png",
      route: "/assessment-builder",
    },
    {
      title: "Collaboration",
      description: "Berkolaborasi dengan sesama pendidik secara efektif",
      linkText: "Mulai Kolaborasi",
      linkColor: "text-[#6b2cfe]",
      bgColor: "bg-[#eee7ff]",
      illustrationImage: "/collaboration-illustration.png",
      route: "/collaboration",
    },
  ];

  // Check for challenge dialog display conditions
  useEffect(() => {
    // Check if user just registered
    if (justRegistered) {
      setShowChallengeDialog(true);
      setJustRegistered(false); // Clear the flag immediately
      return;
    }

    // Check for first visit of the day
    const today = new Date().toDateString();
    const lastVisitDate = localStorage.getItem("lastDashboardVisit");

    if (lastVisitDate !== today) {
      // First visit of the day
      setShowChallengeDialog(true);
      localStorage.setItem("lastDashboardVisit", today);
    }
  }, [justRegistered, setJustRegistered]);

  const handleFeatureClick = (route: string) => {
    navigate(route);
  };

  const handleTutorialClick = () => {
    window.open(
      "https://drive.google.com/file/d/1pRPp7NIStrlISm8sFblDSi4eSiOFB4mS/view?usp=drive_link",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleCloseChallengeDialog = () => {
    setShowChallengeDialog(false);
  };

  const handleGoToChallenges = () => {
    setShowChallengeDialog(false);
    navigate("/gamification");
  };

  return (
    <div className="bg-[#eeeff6] w-full min-h-screen flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Main content with top padding to account for fixed navbar */}
      <main className="pt-[70px] px-4 sm:px-6 pb-6 flex-1">
        <div className="w-full p-12 mt-8 mx-auto bg-white rounded-xl border-b border-[#f0eff0] relative">
          {/* Feature cards - Responsive layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 relative z-10 max-w-7xl mx-auto">
            {featureCards.map((card, index) => (
              <Card
                key={`feature-card-${index}`}
                className={`w-full h-[180px] sm:h-[194px] ${card.bgColor} rounded-3xl overflow-hidden border-none shadow-none group cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-300`}
                onClick={() => handleFeatureClick(card.route)}
              >
                <CardContent className="p-4 sm:p-6 flex flex-col justify-between h-full relative">
                  {/* Illustration image - positioned absolutely for cards that have one */}
                  {card.illustrationImage && (
                    <img
                      className="absolute bottom-0 right-0 w-[100px] h-[100px] sm:w-[120px] sm:h-[120px] lg:w-[150px] lg:h-[150px] object-contain transition-transform duration-300 group-hover:scale-105"
                      alt={`${card.title} illustration`}
                      src={card.illustrationImage}
                    />
                  )}

                  <div className="flex flex-col items-start gap-2 relative z-10 max-w-[180px] sm:max-w-[206px]">
                    <h2 className="font-medium text-textcolor-1 text-base sm:text-lg lg:text-xl">
                      {card.title}
                    </h2>
                    <p className="font-normal text-textcolor-1 text-xs sm:text-sm tracking-[-0.28px] leading-relaxed">
                      {card.description}
                    </p>
                  </div>

                  <div className="inline-flex items-center gap-2 mt-3 sm:mt-4 relative z-10">
                    <span
                      className={`${card.linkColor} font-medium text-sm sm:text-base underline whitespace-nowrap group-hover:font-bold transition-all duration-300`}
                    >
                      {card.linkText}
                    </span>
                    <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tutorial button with enhanced UI/UX - Responsive sizing */}
          <Button
            onClick={handleTutorialClick}
            className="py-6 px-12 mx-auto mt-8 sm:mt-12 lg:mt-16 rounded-2xl text-white font-bold text-sm sm:text-base bg-[linear-gradient(90deg,rgba(106,44,254,1)_0%,rgba(1,15,254,1)_100%)] hover:opacity-90 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group relative flex items-center justify-center"
          >
            <span className="group-hover:scale-105 transition-transform duration-300">
              Lihat Tutorial
            </span>
          </Button>
        </div>
      </main>

      {/* Challenge Dialog */}
      <ChallengeDialog
        isOpen={showChallengeDialog}
        onClose={handleCloseChallengeDialog}
        onGoToChallenges={handleGoToChallenges}
      />
    </div>
  );
};
